<template>
  <div class="todays-reservations">
    <div class="filters">
      <input
        type="text"
        v-model="filters.search"
        :placeholder="$t('dashboardReservations.searchClients')" />

      <span
        v-if="workersList.length"
        class="separator"></span>

      <select
        v-if="workersList.length"
        v-model="filters.worker">
        <option key="worker-wl-00" value="">{{ $t('common.ui.allWorkers') }}</option>
        <option
          v-for="workerData of workersList"
          :key="'worker-wl-' + workerData.id"
          :value="workerData.id">
          {{ workerData.name }}
        </option>
      </select>

      <span
        v-if="servicesList.length"
        class="separator"></span>

      <select
        v-if="servicesList.length"
        v-model="filters.service">
        <option key="services-list-group-00" value="">{{ $t('common.ui.allServices') }}</option>
        <template
          v-for="(servicesCategory, index) of servicesCategories"
          v-if="servicesInCategory(servicesCategory.id).length">
          <option :key="'services-list-group-' + index" :value="'c' + servicesCategory.id">
            {{ servicesCategory.name }}
          </option>
          <option
            v-for="(service, subindex) of servicesInCategory(servicesCategory.id)"
            :value="service.id"
            :key="'services-list-group-' + index + '-' + subindex">
            &mdash; {{ service.name }}
          </option>
        </template>
      </select>

      <span class="separator"></span>

      <btn
        @click.native="clearFilters"
        type="danger with-icon"
        iconName="cross"
        iconSize="xxxs"
        iconColor="color-light"
        :disabled="!filterIsActive">
        {{ $t('common.ui.clearFilters') }}
      </btn>
    </div>

    <message
      v-if="lastUpdate && lastDataDownload && lastUpdate > lastDataDownload"
      type="info">
      {{ $t('dashboard.updateRequired') }}
      <a
        v-if="!dataAreLoading"
        href="#"
        @click.prevent="loadReservations">
        {{ $t('dashboard.updateData') }}
      </a>
      <a
        v-else
        href="#"
        @click.prevent="false">
        {{ $t('dashboard.updatingData') }}
      </a>
    </message>

    <div class="todays-reservations-block">
      <div class="todays-reservations-header">
        <h3>{{ $t('dashboardReservations.todaysReservations') }}</h3>

        <span
          v-if="pastReservationsCount && showOnlyUpcomingReservationsSwitcher"
          class="todays-reservations-past-counter">
          {{ $t('dashboardReservations.tookPlace', { tookPlace: pastReservationsWordVariations(pastReservationsCount), count: pastReservationsCount, reservationWord: reservationWordVariations(pastReservationsCount) }) }}
          <a href="#" @click.prevent="showOnlyUpcomingReservationsSwitcher = false;">{{ $t('dashboardReservations.show') }}</a>
        </span>

        <label class="todays-reservations-switcher">
          {{ $t('dashboardReservations.showOnlyUpcomingReservations') }}
          <switcher
            key="show-only-upcoming-reservations-switcher"
            v-model="showOnlyUpcomingReservationsSwitcher" />
        </label>
      </div>

      <div
        v-if="reservations.length && paginationMeta && paginationMeta.total"
        :class="{
          'todays-reservations-list': true,
          'is-loading': dataAreLoading
        }">
        <reservation
          v-for="(reservationData, index) in reservations"
          :reservationData="reservationData"
          :key="'trl-' + index"
          :isReservation="true"
          :isBlock="isMobile || isTablet"
          :useInProgress="true"
          :reservationHoursFirst="true" />
      </div>
    </div>

    <pagination
      v-if="paginationMeta && reservations.length"
      ref="pagination"
      :max="paginationMeta.last_page"
      :current="paginationCurrent"
      :meta="paginationMeta"
      :onChange="updatePagination"
      :activeFilters="filterIsActive" />

    <div
      v-if="!todaysReservationsLoaded"
      class="todays-reservations-empty-state">
      {{ $t('dashboardReservations.loadingReservations') }}
    </div>

    <div
      v-if="paginationMeta && !paginationMeta.total && todaysReservationsLoaded"
      class="todays-reservations-empty-state">
      <template v-if="!filterIsActive">{{ $t('dashboardReservations.noUpcomingReservations') }}</template>
      <template v-if="filterIsActive">{{ $t('dashboardReservations.noUpcoimngReservationsForGivenFilters') }}</template>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import Configuration from './../mixins/Configuration';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Pagination from './../common/Pagination';
import Reservation from './Reservation';
import Responsive from './../mixins/Responsive';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'dashboard-todays-reservations',
  mixins: [
    Configuration,
    Responsive,
    WordVariations
  ],
  props: [
    'showOnlyUpcomingReservations',
    'services',
    'servicesCategories',
    'servicesLoaded',
    'workers',
    'workersLoaded'
  ],
  components: {
    'pagination': Pagination,
    'reservation': Reservation
  },
  data () {
    return {
      filters: {
        worker: '',
        service: '',
        search: ''
      },
      showOnlyUpcomingReservationsSwitcher: false,
      reservations: [],
      todaysReservationsLoaded: false,
      paginationMeta: null,
      paginationCurrent: 1,
      dataAreLoading: false,
      pastReservationsCount: 0,
      lastUpdate: null,
      lastDataDownload: null
    }
  },
  computed: {
    filterIsActive () {
      return this.filters.worker !== '' || this.filters.service !== '' || this.filters.search !== '';
    },
    servicesList () {
      return this.services.map(service => ({
        id: service.id,
        name: service.name,
        category_id: service.category_id
      }));
    },
    workersList () {
      return this.workers.map(worker => ({
        id: worker.id,
        name: worker.name
      }));
    }
  },
  watch: {
    showOnlyUpcomingReservationsSwitcher: function (newValue) {
      this.$bus.$emit('show-only-upcoming-reservations-change', newValue);
      this.debouncedLoadReservations();
    },
    'filters': {
      handler: function () {
        this.dataAreLoading = true;
        this.debouncedLoadReservations();

        if (this.$refs.pagination) {
          this.paginationCurrent = 1;
          this.$refs.pagination.reset();
        }
      },
      deep: true
    }
  },
  mounted () {
    this.showOnlyUpcomingReservationsSwitcher = this.showOnlyUpcomingReservations;
    this.debouncedLoadReservations();
    this.$bus.$on('dashboard-reservations-list-reload-needed', this.debouncedLoadReservations);
    this.$bus.$on('dashboard-reservations-list-tab-today-needs-update', this.updateRequired);
  },
  methods: {
    clearFilters () {
      this.filters.worker = '';
      this.filters.service = '';
      this.filters.search = '';

      if (this.$refs.pagination) {
        this.paginationCurrent = 1;
        this.$refs.pagination.reset();
      }
    },
    debouncedLoadReservations: debounce(function () {
      this.loadReservations();
    }, 500),
    loadReservations () {
      this.dataAreLoading = true;

      let queryString = {
        page: this.paginationCurrent
      };

      if (this.filters.worker) {
        queryString.worker_id = this.filters.worker;
      }

      if (this.filters.service) {
        queryString.service_id = this.filters.service;
      }

      if (this.filters.search) {
        queryString.search = this.filters.search;
      }

      if (this.showOnlyUpcomingReservationsSwitcher) {
        queryString.only_future = 1;
      } else {
        queryString.only_future = 0;
      }

      this.$http.get('/panel/calendar/get-today-reservations', { params: queryString }).then(response => {
        this.reservations = response.data.data;
        this.paginationMeta = response.data.meta;
        this.pastReservationsCount = response.data.total_past;
        this.todaysReservationsLoaded = true;

        // Update counter on load of the newest data without filters
        if (!this.filterIsActive) {
          if (this.showOnlyUpcomingReservationsSwitcher) {
            this.$bus.$emit('dashboard-update-todays-reservations-count', this.paginationMeta.total + this.pastReservationsCount);
          } else {
            this.$bus.$emit('dashboard-update-todays-reservations-count', this.paginationMeta.total);
          }
        }

        this.dataAreLoading = false;
        this.lastDataDownload = +new Date();

        if (this.paginationMeta.last_page && this.paginationMeta.current_page > this.paginationMeta.last_page) {
          this.updatePagination(this.paginationMeta.last_page);
        }
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/calendar/get-today-reservations', this.$router);
        this.todaysReservationsLoaded = true;
        this.dataAreLoading = false;
      });
    },
    updatePagination (nextPage) {
      this.dataAreLoading = true;
      this.paginationCurrent = parseInt(nextPage, 10);
      this.debouncedLoadReservations();
    },
    servicesInCategory (categoryID) {
      return this.servicesList.filter(service => service.category_id === categoryID);
    },
    updateRequired () {
      this.lastUpdate = +new Date();
    }
  },
  beforeDestroy () {
    this.$bus.$off('dashboard-reservations-list-reload-needed', this.debouncedLoadReservations);
    this.$bus.$off('dashboard-reservations-list-tab-today-needs-update', this.updateRequired);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.todays-reservations {
  &-block {
    background: $color-light;
    border: 1px solid $color-border;
    margin-bottom: 1rem;
    padding: 2.5rem;
  }

  &-header {
    align-items: center;
    display: flex;
  }

  &-switcher {
    align-items: center;
    display: flex;
    margin-left: auto;

    .switcher {
      margin-left: 1rem;
    }
  }

  &-empty-state {
    color: $color-text-light;
    padding: 4rem 0;
    text-align: center;
  }

  h3 {
    font-size: 2.4rem;
    font-weight: 400;
    line-height: 1;
    margin: 0;
  }

  &-list {
    display: flex;
    flex-wrap: wrap;
    margin: 2.6rem -2.6rem -2.5rem -2.6rem;
    width: auto;

    &.is-loading {
      opacity: .25;
    }

    & + .todays-filtered-reservations-empty-state {
      color: $color-text-light;
      display: block;
      padding: 2rem 0 0 0;
      text-align: center;
    }
  }

  &-past-counter {
    color: $color-text-light;
    margin-left: 2rem;
  }
}

.filters {
  display: flex;
  margin-bottom: 1rem;
  margin-top: -4.3rem;
  padding: 2rem 0;
  width: 100%;

  .button {
    width: 16rem;
  }

  select {
    height: 3.6rem;
    line-height: 3.6rem;
    min-width: 24rem;
    width: 24rem;
  }

  input {
    background: $color-light url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill-rule="evenodd" fill="%237F8FA4" d="M382.217,267.717a1,1,0,0,1-1.414,0l-3.15-3.15a7,7,0,1,1,1.415-1.414l3.149,3.15A1,1,0,0,1,382.217,267.717Zm-5.2-12.271a5,5,0,1,0,0,7.071A5,5,0,0,0,377.017,255.446Z" transform="translate(-366.5 -252)"/></svg>');
    background-repeat: no-repeat;
    background-position: calc(100% - 1.2rem) center;
    border: 1px solid $color-border;
    border-radius: $border-radius-small;
    height: 3.6rem;
    padding: 0 1rem;
    width: 100%;
  }

  .separator {
    display: block;
    height: 3.2rem;
    position: relative;
    min-width: 2.5rem;
    width: 2.5rem;

    &:before {
      background: $color-text-lightest;
      content: "";
      height: 24px;
      left: 12px;
      position: absolute;
      top: 6px;
      width: 1px;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all .25s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: scale(.5);
}

@media (max-width: 1040px) {
  .todays-reservations {
    .filters {
      background: $color-bg-medium;
      flex-wrap: wrap;
      margin: -2.6rem -1.5rem 2rem -1.5rem;
      padding: 1rem 1.5rem 2rem 1.5rem;
      width: calc(100% + 3rem);

      & > .separator {
        display: none;
      }

      & > select,
      & > input,
      & > .button {
        margin-bottom: 1rem;
        width: 100%;
      }

      & > .button {
        margin: 0;
      }
    }

    .todays-reservations-header {
      flex-wrap: wrap;

      h3 {
        width: 100%;
      }

      .todays-reservations-past-counter {
        margin: 0;
        width: 50%;

        & + .todays-reservations-switcher {
          width: 50%;
        }
      }

      .todays-reservations-switcher {
        justify-content: flex-end;
        margin: 0;
        width: 100%;
      }
    }

    .todays-reservations-list {
      margin: 0 -1.25rem 1rem -1.25rem;

      .reservation {
        margin: 2.5rem 1.25rem 0 1.25rem;
        max-width: calc(50% - 2.5rem);
      }
    }
  }
}

@media (max-width: 720px) {
  .todays-reservations {
    .todays-reservations-header {
      h3,
      .todays-reservations-past-counter,
      .todays-reservations-switcher {
        justify-content: flex-start;
        padding: .75rem 0;
        width: 100%!important;
      }

      h3 {
        padding-top: 0;
      }
    }
  }
}

@media (max-width: 680px) {
  .todays-reservations {
    .todays-reservations-block {
      padding: 2.5rem 1.5rem;
    }

    .todays-reservations-day-label {
      font-size: 1.5rem;

      .counter {
        top: 0;
      }
    }

    .todays-reservations-day {
      margin: 0 -1.5rem;
      padding: 0 1.5rem;
    }

    .todays-reservations-list {
      margin: 0 0 1rem 0;

      .reservation {
        margin: 2.5rem 0 0 0;
        max-width: 100%;
      }
    }
  }
}

@media (max-width: 480px) {
  .todays-reservations {
    .todays-reservations-header {
      .todays-reservations-past-counter,
      .todays-reservations-switcher {
        font-size: 1.3rem;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/DashboardTodaysReservations.vue